import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
  DirectGrantRequest, DirectGrantResult, EmployeeLinkInviteRequest, RoleGrant, RoleGrantInvitation,
  RoleGrantInviteRequest, SelfGrantRequest, SelfGrantResult, SelfGrantSummary
} from './grant.models';

/**
 * The monolith's `/grants` endpoints (`API/Controller/Company/RoleGrantController.cs`).
 *
 * Single `BASE_URL` origin; `AuthInterceptor` attaches the context token and the `CM-Secret` cookie rides
 * along, so nothing here deals with credentials.
 *
 * Every call is company-scoped by path — `companyId` is the axis of the whole screen, not a filter — which is
 * why a session with no company of its own (a platform owner) must choose one before anything can be loaded.
 */
@Injectable({providedIn: 'root'})
export class GrantHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /**
   * Issue an invitation.
   *
   * **The response is the only place the one-time code will ever exist.** The backend stores an unsalted
   * SHA-256 of it, so no later read can return it — see `RoleGrantService.ToDto`, which is handed `code: null`
   * on every path except this one.
   */
  invite(request: RoleGrantInviteRequest): Observable<RoleGrantInvitation> {
    return this.http.post<RoleGrantInvitation>(`${this.baseUrl}/grants/invitations`, request);
  }

  /**
   * **Self-activation** (#279): grants the *calling* platform owner tenant-scoped roles in one company or
   * shard, so the context picker has a tile to offer for a tenant they hold nothing in.
   *
   * Unlike every other method here this one names no target account — the target is the caller, which is the
   * whole point and the reason the route restates `[Authorize(Roles = PlatformOwner)]` at method level. The
   * act is recorded in the audit trail on success **and** on refusal, under category `RoleGrant`, action
   * `SelfGranted`, targeted at the company.
   */
  grantSelf(request: SelfGrantRequest): Observable<SelfGrantResult> {
    return this.http.post<SelfGrantResult>(`${this.baseUrl}/grants/self`, request);
  }

  /**
   * **Direct assignment** (#749): grants an *existing external identity* scoped roles straight away, with no
   * invitation and no acceptance step.
   *
   * The third creation path beside {@link invite} and {@link grantSelf}, and the only one whose subject is
   * neither the caller nor somebody who agreed to anything — which is why it is `PlatformOwner`-only
   * (restated at method level on the route, as `GrantSelf` is) and why `DirectGrantRequest.Reason` is
   * **required** where `SelfGrantRequest.Reason` is optional: the audit entry has to say why one operator
   * handed a third party access.
   *
   * The act is recorded under category `RoleGrant`, action `DirectlyGranted`, **targeted at the identity**
   * whose access changed — not at the company, which is the exception `SelfGranted` makes because its actor
   * and subject are the same principal. Refusals are recorded too, as `Denied`, so a run of refused attempts
   * is findable by one query.
   */
  grantDirect(request: DirectGrantRequest): Observable<DirectGrantResult> {
    return this.http.post<DirectGrantResult>(`${this.baseUrl}/grants/direct`, request);
  }

  /**
   * The calling platform owner's own **active** self-activated grants — what backs "Ihre freigeschalteten
   * Zugänge" on the context chooser. Each one expires on its own; {@link revokeGrant} is how the panel lets
   * the operator take one back early.
   */
  listMine(): Observable<SelfGrantSummary[]> {
    return this.http.get<SelfGrantSummary[]>(`${this.baseUrl}/grants/mine`);
  }

  /** Every invitation issued for a company — pending, redeemed and revoked alike. */
  listInvitations(companyId: string): Observable<RoleGrantInvitation[]> {
    return this.http.get<RoleGrantInvitation[]>(
      `${this.baseUrl}/grants/invitations/${encodeURIComponent(companyId)}`);
  }

  /** Every grant held in a company, active and revoked. */
  listGrants(companyId: string): Observable<RoleGrant[]> {
    return this.http.get<RoleGrant[]>(`${this.baseUrl}/grants/${encodeURIComponent(companyId)}`);
  }

  /**
   * Revoke an invitation — **and the grants it already produced**, if any. That cascade is the backend's
   * behaviour (`RoleGrantService.RevokeInvitation`), not an accident, so the UI must say so before asking.
   */
  revokeInvitation(id: string): Observable<RoleGrantInvitation> {
    return this.http.delete<RoleGrantInvitation>(
      `${this.baseUrl}/grants/invitations/${encodeURIComponent(id)}`);
  }

  /**
   * Re-send a pending invitation by e-mail (#579).
   *
   * **This is withdraw-and-re-issue, not "send the same code again".** The backend stores an unsalted SHA-256
   * of the code, so there is no plaintext left to put in a second mail. `RoleGrantService.ResendInvitation`
   * therefore revokes the invitation, issues a replacement carrying the same scope, roles, label, binding,
   * enforcement and employee, mails that one, and answers with it — a **new id and a new plaintext `code`**,
   * valid 72 hours, plus an `emailDelivery` saying what became of the mail. The old code stops working the
   * moment this call succeeds, which is why every caller confirms first.
   *
   * No body: every field that decides anything is copied from the stored row, which is what makes a re-send
   * safe under the issuing authority — it cannot widen a scope, add a role or drop an enforcement.
   */
  resendInvitation(id: string): Observable<RoleGrantInvitation> {
    return this.http.post<RoleGrantInvitation>(
      `${this.baseUrl}/grants/invitations/${encodeURIComponent(id)}/resend`, {});
  }

  /** Revoke a single grant, leaving any siblings the same invitation created intact. */
  revokeGrant(id: string): Observable<RoleGrant> {
    return this.http.delete<RoleGrant>(`${this.baseUrl}/grants/${encodeURIComponent(id)}`);
  }

  /**
   * Revoke a grant **together with the grants it covers** (#759), in one action.
   *
   * ### This is the single binding point for the multi-revoke contract
   *
   * Route and body shape live here and nowhere else, so a change to the backend's spelling is a change to
   * this one method. Callers pass ids and read back the revoked grants; nothing above knows the wire.
   *
   * ### Why `POST /grants/revoke` and not a widened `DELETE /grants/{id}`
   *
   * A set does not fit that route's single path segment, and a DELETE body is a shape not every client and
   * proxy carries reliably. `RoleGrantController.RevokeGrants` sits on the same controller under the same
   * class attribute, so nothing here is reachable that the single-id route is not.
   *
   * ### Why not loop {@link revokeGrant} in the component
   *
   * Because a partial failure would then be the caller's problem, and "three of five roles were withdrawn,
   * and the screen says it failed" is the worst possible outcome of a privilege change. **All of them or none
   * of them:** every id is authorized individually server-side through `GrantIssuancePolicy.CanIssue` — a
   * bulk revoke must not become a way to reach a grant the caller could not revoke singly — and one refusal
   * refuses the whole call before anything is written. Each grant that IS revoked still files its own audit
   * entry; a bulk revoke is not one event.
   */
  revokeGrants(ids: readonly string[]): Observable<RoleGrant[]> {
    return this.http.post<RoleGrant[]>(`${this.baseUrl}/grants/revoke`, {Ids: [...ids]});
  }

  /**
   * Revoke one of the **caller's own** grants (#279) — the self-scoped sibling of {@link revokeGrant}, for
   * "Ihre freigeschalteten Zugänge" to call directly rather than through the admin-gated endpoint, which
   * 403s any grant-holder who isn't also a `PlatformOwner`. `withCredentials: true` because the endpoint
   * identifies the target grant from the caller's own session (the `CM-Secret` cookie), not from a
   * company-scoped path, matching every other own-session call in this app.
   */
  revokeMyGrant(id: string): Observable<RoleGrant> {
    return this.http.delete<RoleGrant>(`${this.baseUrl}/grants/mine/${encodeURIComponent(id)}`,
      {withCredentials: true});
  }

  // ----- employee-link invitations (#279) -----
  //
  // These two live on `AccountController` rather than `/grants`, because they belong to the employee's
  // authenticator surface: they are what an administering role does INSTEAD of picking an account out of the
  // installation-wide directory, and the company is taken from the session rather than named in the path.
  // Withdrawing one is still `revokeInvitation` above — one revoke endpoint, one place where "whoever could
  // issue it may withdraw it" is decided.

  /**
   * Issue an employee-link invitation: a one-time code that lets the employee bind **their own** external
   * account to this employee record by signing in with it. Confers no role.
   *
   * As with {@link invite}, the response is the only place the code will ever exist.
   */
  inviteEmployeeLink(employeeId: string,
                     request: EmployeeLinkInviteRequest = {}): Observable<RoleGrantInvitation> {
    return this.http.post<RoleGrantInvitation>(
      `${this.baseUrl}/auth/account/admin/employees/${encodeURIComponent(employeeId)}/invitations`, request);
  }

  /** The employee-link invitations issued for one employee — pending, redeemed and revoked alike. */
  listEmployeeInvitations(employeeId: string): Observable<RoleGrantInvitation[]> {
    return this.http.get<RoleGrantInvitation[]>(
      `${this.baseUrl}/auth/account/admin/employees/${encodeURIComponent(employeeId)}/invitations`);
  }

  /**
   * Re-send one employee's employee-link invitation (#579) — the employee-narrowed door onto the same service
   * method {@link resendInvitation} calls, with the same response and the same refusals.
   *
   * **Why a second route rather than pointing the employee screen at the generic one.** The employee screen
   * knows an employee and a row in that employee's own list; it does not know, and must not have to assert,
   * that the id belongs to this employee. `AccountController.ResendEmployeeInvitation` passes the employee id
   * to the service as `expectedEmployeeId`, which refuses anything whose `EmployeeId` differs — so the
   * narrowing is checked by the same code that does the work, not by the caller.
   *
   * That check answers **`E_GRANT_NOT_FOUND`**, exactly as a genuinely unknown id does, and deliberately so:
   * an id from another employee's list, a role-grant invitation's id, and an id that never existed are
   * indistinguishable in the response. The endpoint is therefore not an oracle for "does this invitation
   * exist elsewhere" — which is what it would become if the mismatch had its own code.
   */
  resendEmployeeInvitation(employeeId: string, id: string): Observable<RoleGrantInvitation> {
    return this.http.post<RoleGrantInvitation>(
      `${this.baseUrl}/auth/account/admin/employees/${encodeURIComponent(employeeId)}/invitations/`
      + `${encodeURIComponent(id)}/resend`, {});
  }
}
